/**
 * /people/leavers — Session 3a PR 4.
 *
 * Recent terminations (last 30/60/90 days) with EOS · gov-filings ·
 * clearance status. Tashghil-owned per Session 3a §4.3.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel mounted in the right rail (live fetch)
 *   2. InlineAISuggestion cards (settlement-overdue + pattern)
 *   3. TraceableValue wrapping settlement_amount + days_since_term
 *      + clearance_progress (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, Plus, TrendingDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { session3aApi, type LeaverItem } from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { formatSar, severityChip, showAlertTrace } from "./_session3aHelpers";

const DAYS_WINDOWS = [30, 60, 90] as const;
const SETTLEMENT_OVERDUE_DAYS = 30;

function reasonTone(code: string): "neutral" | "attention" | "critical" {
  if (code === "resignation" || code === "mutual_consent" || code === "fixed_term_end")
    return "neutral";
  if (code === "termination_with_cause" || code === "absconded") return "attention";
  if (code === "without_cause" || code === "deceased") return "critical";
  return "neutral";
}

function filingChip(state: "sent" | "pending" | "na"): string {
  if (state === "sent") return "bg-emerald-100 text-emerald-800";
  if (state === "pending") return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-500";
}

export function LeaversPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [days, setDays] = useState<number>(30);
  const [filter, setFilter] = useState<"all" | "eos_unpaid" | "clearance_incomplete" | "filings_pending">(
    "all",
  );

  const list = useQuery({
    queryKey: ["leavers", legalEntityId, days],
    queryFn: () => session3aApi.listLeavers(legalEntityId ?? "", days),
    enabled: !!legalEntityId,
  });

  const items: LeaverItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const unpaidSettlement = items.filter(
      (r) =>
        r.eos_state !== "paid" &&
        r.eos_state !== "closed" &&
        r.days_since_termination > SETTLEMENT_OVERDUE_DAYS,
    ).length;
    const clearanceIncomplete = items.filter(
      (r) => r.clearance_items_complete < r.clearance_items_total,
    ).length;
    const filingsPending = items.filter((r) =>
      Object.values(r.gov_filings).some((s) => s === "pending"),
    ).length;
    const overdue = items.find(
      (r) =>
        r.eos_state !== "paid" &&
        r.days_since_termination > SETTLEMENT_OVERDUE_DAYS,
    );
    const reasonCounts: Record<string, number> = {};
    items.forEach((r) => {
      reasonCounts[r.reason_code] = (reasonCounts[r.reason_code] ?? 0) + 1;
    });
    const topReason = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0];
    return {
      total: items.length,
      unpaidSettlement,
      clearanceIncomplete,
      filingsPending,
      overdue,
      topReason: topReason ? { code: topReason[0], count: topReason[1] } : null,
    };
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "eos_unpaid")
      return items.filter(
        (r) =>
          r.eos_state !== "paid" &&
          r.eos_state !== "closed" &&
          r.days_since_termination > SETTLEMENT_OVERDUE_DAYS,
      );
    if (filter === "clearance_incomplete")
      return items.filter((r) => r.clearance_items_complete < r.clearance_items_total);
    if (filter === "filings_pending")
      return items.filter((r) =>
        Object.values(r.gov_filings).some((s) => s === "pending"),
      );
    return items;
  }, [items, filter]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("people.leavers.title", "Leavers")}
          subtitle={t(
            "people.leavers.subtitle",
            "Recent terminations with EOS · gov-filings · clearance status. Tashghil tracks the unwind.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {DAYS_WINDOWS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  days === d
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`people.leavers.filter.last_${d}_days`, `Last ${d} days`)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setFilter(filter === "eos_unpaid" ? "all" : "eos_unpaid")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "eos_unpaid"
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("people.leavers.filter.eos_unpaid", "EOS unpaid")}
              {stats.unpaidSettlement > 0 && (
                <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-semibold text-white">
                  {stats.unpaidSettlement}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() =>
                setFilter(filter === "clearance_incomplete" ? "all" : "clearance_incomplete")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "clearance_incomplete"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("people.leavers.filter.clearance_incomplete", "Clearance incomplete")}
            </button>
            <button
              type="button"
              onClick={() =>
                setFilter(filter === "filings_pending" ? "all" : "filings_pending")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "filings_pending"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("people.leavers.filter.filings_pending", "Gov filings pending")}
            </button>
          </div>
          <Link
            to="/operations/transactions/eos/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("people.leavers.cta.initiate", "Initiate Termination")}
          </Link>
        </div>

        {stats.overdue && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<AlertTriangle className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "people.leavers.suggestion.overdue_headline",
              `Settlement overdue: ${stats.overdue.employee_first_name_en} ${stats.overdue.employee_last_name_en} terminated ${stats.overdue.days_since_termination} days ago — Article 88 timeline breach risk.`,
            )}
            body={t(
              "people.leavers.suggestion.overdue_body",
              "Mutaba'aa would auto-flag once threshold crossed. Process the EOS now to clear exposure.",
            )}
            primaryAction={{
              label: t("people.leavers.suggestion.process_now", "Process now"),
              onClick: () => {
                window.location.assign(`/operations/eos?employee_id=${stats.overdue!.employee_id}`);
              },
            }}
            suggestionKey={`leavers-overdue-${stats.overdue.employee_id}`}
          />
        )}
        {stats.topReason && stats.topReason.code === "resignation" && stats.topReason.count >= 3 && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<TrendingDown className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "people.leavers.suggestion.pattern_headline",
              `Pattern: ${stats.topReason.count} voluntary resignations in the past ${days} days.`,
            )}
            body={t(
              "people.leavers.suggestion.pattern_body",
              "Retention review suggested. Open Reports → Turnover for the breakdown.",
            )}
            primaryAction={{
              label: t("people.leavers.suggestion.open_turnover", "Open Turnover report"),
              onClick: () => {
                window.location.assign("/reports/turnover");
              },
            }}
            suggestionKey={`leavers-pattern-resignation-${days}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("people.leavers.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">
                    {t("people.leavers.col.termination_date", "Termination date")}
                  </th>
                  <th className="px-3 py-2">{t("people.leavers.col.reason", "Reason")}</th>
                  <th className="px-3 py-2">{t("people.leavers.col.eos_state", "EOS State")}</th>
                  <th className="px-3 py-2">{t("people.leavers.col.settlement", "Settlement (SAR)")}</th>
                  <th className="px-3 py-2">{t("people.leavers.col.clearance", "Clearance")}</th>
                  <th className="px-3 py-2">{t("people.leavers.col.gov_filings", "Gov filings")}</th>
                  <th className="px-3 py-2">{t("common.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.employee_id}
                    className="group border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_first_name_en} {row.employee_last_name_en}
                      </Link>
                      <div className="text-xs text-text-tertiary">{row.employee_code}</div>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <div dir="ltr">{row.termination_date}</div>
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "people.leavers.trace.days_since",
                              `days_since_termination = today − termination_date (${row.termination_date}). Drives the Article 88 settlement-deadline clock.`,
                            ),
                          )
                        }
                      >
                        <span className="text-xs text-text-tertiary">
                          {row.days_since_termination} {t("people.leavers.days_ago", "days ago")}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(reasonTone(row.reason_code))}`}
                      >
                        {row.reason_label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">{row.eos_state}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "people.leavers.trace.settlement",
                              `settlement = gratuity (Article 84/85) + leave_encashment + ticket_entitlement − outstanding_loans. Engine audit_trace captures each layer.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr" className="text-text-primary">
                          {formatSar(row.settlement_amount_sar)}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "people.leavers.trace.clearance",
                              `clearance_progress = items_complete / items_total. Default seed: asset_return · knowledge_handover · accounts_deactivation · final_documents.`,
                            ),
                          )
                        }
                      >
                        <span className="text-xs text-text-secondary">
                          {row.clearance_items_complete} / {row.clearance_items_total}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {(["qiwa", "gosi", "iqama"] as const).map((kind) => (
                          <span
                            key={kind}
                            className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium ${filingChip(row.gov_filings[kind])}`}
                            title={`${kind}: ${row.gov_filings[kind]}`}
                          >
                            {kind.toUpperCase()}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <Link
                          to={`/operations/eos?employee_id=${row.employee_id}`}
                          className="rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          {t("people.leavers.action.open_eos", "Open EOS")}
                          <ChevronRight className="ms-0.5 inline h-3 w-3" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "people.leavers.empty",
              `No terminations in the last ${days} days. ✦ Tashghil notes: workforce stable.`,
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: stats.total,
            attention: stats.clearanceIncomplete + stats.filingsPending,
            critical: stats.unpaidSettlement,
            top_kind: t("people.leavers.title", "Leavers"),
            window_label: `last ${days} days`,
          }}
        />
      </aside>
    </div>
  );
}
